/**
 * The mesh exporter's input contract.
 *
 * This is deliberately a PLAIN data shape with no THREE types and no app
 * state: an exporter that reached into `state.pieces` or called
 * `mesh.updateMatrixWorld()` could not be lifted anywhere. Phase 4 of the port
 * plan moves `buildGLB`/`buildOBJ` into the host as
 * `ctx.graphics.exportMesh`, and the only thing that has to travel with them
 * is this file.
 *
 * The caller flattens its scene into `MeshPiece[]` (see
 * `export/collect.ts`) and the writers do the rest.
 */

/** One draw group of a piece: a vertex range and the colour it renders with. */
export type MeshGroup = {
  /** First VERTEX (not float, not triangle) of the range. */
  start: number;
  /** Vertex count. */
  count: number;
  /** "#rrggbb" — already resolved through paint overrides and the palette. */
  color: string;
  /** Palette slot key, used for material names and the metal/flame/water specials. */
  slot: string;
};

/** One placed piece, flattened: local geometry plus its world transform. */
export type MeshPiece = {
  /** Stable identifier — becomes part of the glTF node name. */
  id: number;
  /** Part id, e.g. "tower_round". Names the mesh. */
  kind: string;
  /** Non-indexed XYZ triples in LOCAL space. */
  position: Float32Array;
  /** Matching per-vertex normals. */
  normal: Float32Array;
  groups: MeshGroup[];
  /** Local-space bounding box, for centring without touching a scene graph. */
  box: { min: number[]; max: number[] };
  pos: number[];
  rotY: number;
  scale: number;
  /**
   * Dedup key. Two pieces with the same key share one glTF mesh in `nodes`
   * mode — same part, same params, same paint.
   */
  dedupKey: string;
};

export type MeshExportOptions = {
  /** `merged` bakes world transforms into one mesh; `nodes` keeps a hierarchy. */
  mode: "merged" | "nodes";
  /** Bake colour into COLOR_0 and emit a single white material. */
  vertexColors: boolean;
  /** Centre the footprint on the origin and drop the base to y = 0. */
  center: boolean;
  /** Uniform output scale. 1 unit = 1 metre at scale 1. */
  scale: number;
  /** Base filename, without extension. */
  name: string;
  /** `asset.generator` string written into the glTF. */
  generator: string;
};

export type MeshExportInfo = {
  verts: number;
  prims: number;
  mats: number;
  nodes: number;
  bytes: number;
};

export const DEFAULT_EXPORT_OPTIONS: MeshExportOptions = {
  mode: "merged",
  vertexColors: false,
  center: true,
  scale: 1,
  name: "kitwright-structure",
  generator: "ArcLog OS",
};
